/**
 * Interactive track card — used by the homepage catalogue (Music component)
 * and every SPA catalog page (/music, /music/{section}, /genre/{slug}).
 *
 * Also exports `useCatalog(section, genre)` which handles fetching, buying,
 * cart, download and preview playback so pages stay slim.
 */
import { musicLine } from "@/lib/musicAttrs";
import { useCurrency } from "@/lib/CurrencyContext";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Play, Pause, ShoppingCart, ShoppingBag, Download, Loader2, Check, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, ASSET_BASE, ASSETS, resolveCoverUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useCart } from "@/lib/CartContext";
import { formatTrackTitle, trackCardMetaLabel, trackDownloadName } from "@/lib/trackTitle";
import { downloadAuthenticatedBlob, fetchTrackDownloadBlob } from "@/lib/download";
import { ShareButton } from "@/components/ShareModal";
import { normalizeCheckoutError } from "@/lib/checkoutError";
import { useVerificationGate } from "@/lib/VerificationGate";
import { useShoppingSession } from "@/lib/ShoppingSession";
import { fireSnapStartCheckout } from "@/components/TrackingLoader";


export function TrackCard({ track, index, playingId, onPlayToggle, onBuy, onDownload, onAddToCart, buying, inCart, downloadingId }) {
  const { format: formatPrice, converted } = useCurrency();
  const { t } = useTranslation();
  const isPlaying = playingId === track.id;
  const displayTitle = formatTrackTitle(track);
  // Prefer the dedicated track page when a slug exists (Stage 9). Fall back
  // to `/#music` for legacy tracks that haven't been backfilled yet.
  const trackHref = track.track_slug ? `/track/${track.track_slug}` : null;
  const shareUrl =
    typeof window !== "undefined" && track.track_slug
      ? `${window.location.origin}/track/${track.track_slug}`
      : "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.55, delay: (index % 6) * 0.05 }}
      className="card-lift hairline rounded-2xl overflow-hidden bg-white/[0.02] flex flex-col"
      data-testid={`track-card-${track.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-[#181818] via-[#0d0d0d] to-[#1c0808]">
        <img
          src={resolveCoverUrl(track.cover_url)}
          onError={(e) => { e.currentTarget.src = ASSETS.trackFallbackCover; }}
          alt={`${displayTitle} — ${track.genre || "DJ SPY track"} cover art`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <button
          onClick={() => onPlayToggle(track)}
          data-testid={`track-play-${track.id}`}
          className={`absolute bottom-2.5 right-2.5 w-11 h-11 rounded-full flex items-center justify-center transition-transform hover:scale-105 ${
            isPlaying ? "bg-red-brand pulse-red" : "bg-white"
          }`}
          aria-label={isPlaying ? "pause" : "play preview"}
        >
          {isPlaying ? (
            <Pause size={16} className="text-white" />
          ) : (
            <Play size={16} className="text-black translate-x-0.5" />
          )}
        </button>
        {shareUrl && (
          <div className="absolute top-2.5 right-2.5">
            <ShareButton
              url={shareUrl}
              title={displayTitle}
              description={track.description || `${displayTitle} — DJ SPY`}
              variant="icon"
              size="sm"
              testid={`track-share-${track.id}`}
              label="Share track"
            />
          </div>
        )}
        {track.owned && (
          <div className="absolute top-2.5 left-2.5 bg-black/70 backdrop-blur border border-red-brand text-red-brand text-[9px] font-mono tracking-[0.25em] px-2 py-0.5 rounded-full">
            {t("trackCard.owned")}
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col flex-1 gap-2">
        <div className="font-mono text-[9px] tracking-[0.25em] text-red-brand truncate">
          {String(index + 1).padStart(2, "0")} / {trackCardMetaLabel(track)}
        </div>
        {(track.bpm || track.camelot) && (
          <div className="font-mono text-[9px] tracking-[0.2em] text-white/50" dir="ltr" data-testid={`track-bpm-key-${track.id}`}>
            {musicLine(track)}
          </div>
        )}
        {trackHref ? (
          <Link
            to={trackHref}
            data-testid={`track-title-link-${track.id}`}
            className="font-display text-base text-white leading-snug line-clamp-2 hover:text-red-brand transition-colors"
            title={displayTitle}
          >
            {displayTitle}
          </Link>
        ) : (
          <div
            className="font-display text-base text-white leading-snug line-clamp-2"
            title={displayTitle}
          >
            {displayTitle}
          </div>
        )}
        {track.blog_slug && (
          <Link
            to={`/blog/${track.blog_slug}`}
            data-testid={`track-story-${track.id}`}
            className="inline-flex items-center gap-1 text-[9px] font-mono tracking-[0.15em] text-white/50 hover:text-red-brand transition-colors"
            aria-label={`Read the story behind ${displayTitle}`}
          >
            <BookOpen size={10} /> STORY <span aria-hidden>→</span>
          </Link>
        )}
        <div className="mt-auto pt-2 flex items-center justify-between gap-2 border-t border-white/[0.06]">
          <div className={`font-anton leading-none text-white whitespace-nowrap ${converted ? "text-sm sm:text-base" : "text-lg"}`} dir="ltr" data-testid={`track-card-price-${track.id}`}>
            {track.price > 0 ? formatPrice(track.price) : <span className="text-red-brand">FREE</span>}
          </div>
          {track.owned ? (
            <button
              onClick={() => onDownload(track)}
              disabled={downloadingId === track.id}
              data-testid={`track-download-${track.id}`}
              className="btn-ghost !py-1.5 !px-2.5 text-[11px] disabled:opacity-60 disabled:cursor-wait"
              aria-label={t("trackPage.downloadFull")}
            >
              <Download size={12} /> {downloadingId === track.id ? t("library.preparing") : t("trackCard.download")}
            </button>
          ) : (
            <div className="flex items-center gap-1">
              {track.price > 0 && (
                <button
                  onClick={() => onAddToCart(track)}
                  disabled={inCart}
                  data-testid={`track-add-cart-${track.id}`}
                  className="btn-ghost !py-1.5 !px-2 text-[11px]"
                  aria-label={inCart ? "In cart" : "Add to cart"}
                  title={inCart ? "In cart" : "Add to cart"}
                >
                  {inCart ? <Check size={12} className="text-red-brand" /> : <ShoppingBag size={12} />}
                </button>
              )}
              <button
                onClick={() => onBuy(track)}
                disabled={buying === track.id}
                data-testid={`track-buy-${track.id}`}
                className="btn-primary !py-1.5 !px-2.5 text-[11px]"
              >
                {buying === track.id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <ShoppingCart size={12} />
                )}
                {track.price > 0 ? t("trackCard.buy") : t("trackCard.get")}
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Handles preview playback, cart add, buy and download flows.
 * Returns everything a page needs to render a `<TrackCard/>` grid.
 */
export function useTrackActions() {
  const { t } = useTranslation();
  const [playingId, setPlayingId] = useState(null);
  const [buying, setBuying] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useCart();
  const { openVerificationGate } = useVerificationGate();
  const { termsAccepted: sharedTermsAccepted, nudgeTerms } = useShoppingSession();

  const onPlayToggle = (track) => {
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    const url = `${ASSET_BASE}/api/tracks/${track.id}/preview`;
    setPlayingId(track.id);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().catch(() => {});
      }
    }, 30);
  };

  const onAddToCart = (track) => {
    if (track.owned) { toast.info("You already own this track."); return; }
    if (cart.hasId(track.id)) { toast.info("Already in your cart."); cart.open(); return; }
    if ((cart.packs || []).some((p) => (p.track_ids || []).includes(track.id))) {
      toast.info(t("packs.trackInAlbum")); cart.open(); return;
    }
    const added = cart.addItem(track);
    if (added) {
      toast.success(`Added "${formatTrackTitle(track)}" to cart`);
      cart.open();
    }
  };

  const onBuy = async (track, { termsAccepted, onDone } = {}) => {
    // Double-tap / rerender guard.
    if (buying === track.id) return;
    if (!user) {
      toast("Sign in to purchase", { description: "Create an account or sign in to buy tracks." });
      navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      return;
    }
    // Prefer the shared shopping-session terms state — callers may still
    // pass a per-page `termsAccepted` override for backward compatibility.
    const accepted = sharedTermsAccepted || Boolean(termsAccepted);
    if (!accepted) {
      nudgeTerms();
      return;
    }
    setBuying(track.id);
    try {
      const { data } = await api.post("/checkout/session", {
        track_id: track.id,
        origin_url: window.location.origin,
        terms_accepted: true,
        policy_version: "2026-01-11",
      });
      if (data.free) {
        toast.success("Track added to your library");
        onDone && (await onDone());
      } else if (data.url) {
        // Fire Snapchat START_CHECKOUT ONLY after Stripe returned a
        // valid checkout URL — never on the button click itself.
        // Snapchat pixel is consent-gated at install time; if
        // advertising consent is denied, `window.snaptr` is not
        // installed and this is a silent no-op.
        //
        // Value MUST come from the server-authoritative response
        // (`data.amount`, the same AED total sent to Stripe) — never
        // from the client-side `track.price` which can be stale or
        // undefined at click time.  Fallback of 0 is only used if the
        // server somehow omits the field, which is defensive; the
        // backend contract guarantees it.
        try {
          fireSnapStartCheckout({
            value: Number(data.amount ?? 0),
            currency: (data.currency || "AED").toUpperCase(),
            productIds: [track.id],
            numItems: 1,
          });
        } catch { /* noop */ }
        window.location.href = data.url;
      }
    } catch (err) {
      const norm = normalizeCheckoutError(err, "Checkout failed");
      if (norm.isEmailNotVerified) {
        openVerificationGate({
          email: user?.email,
          action: track.price > 0 ? "purchase" : "claim",
        });
      } else {
        toast.error(norm.message);
      }
    } finally {
      setBuying(null);
    }
  };

  const onDownload = async (track) => {
    // Double-tap guard — silently ignore a repeat click while a
    // download ticket is being minted / blob is being fetched.
    if (downloadingId) return;
    setDownloadingId(track.id);
    try {
      await downloadAuthenticatedBlob({
        trackId: track.id,
        fetchBlob: () => fetchTrackDownloadBlob(track.id),
        filename: `${trackDownloadName(track)}.mp3`,
        onError: async (err) => {
          if (err?.response?.status === 403) {
            let detail = err?.response?.data;
            try {
              if (detail instanceof Blob) detail = JSON.parse(await detail.text());
            } catch (_) {}
            if (detail?.detail?.code === "email_not_verified") {
              toast.error(t("verifyEmail.actionDownload"));
              return true;
            }
          }
          toast.error("Download failed");
          return true;
        },
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const AudioElement = () => (
    <audio
      ref={audioRef}
      data-testid="global-audio"
      onEnded={() => setPlayingId(null)}
      onTimeUpdate={(e) => {
        if (e.currentTarget.currentTime >= 30) {
          e.currentTarget.pause();
          setPlayingId(null);
        }
      }}
    />
  );

  return { playingId, buying, downloadingId, onPlayToggle, onAddToCart, onBuy, onDownload, AudioElement };
}
